import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Plus, Search } from "lucide-react";
import InterviewList from "./InterviewList";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppButton,
  FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField, useListFilters,
} from "../ui";
import "./interviews.scss";

const PAGE_SIZE = 10;

const InterviewsPage = () => {
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const navigate = useNavigate();

  /* The FilterPanel pattern (D3), replacing an inline `d-none d-md-block` strip
     PLUS a `d-md-none` button-and-modal rendering the same two filters twice.
     The options arrive with the FIRST response (the API returns its own filter
     vocabulary alongside the rows), which is exactly the async case
     useListFilters reads its spec through a ref for. */
  const listFilters = useListFilters({
    currentStatus: {
      label: "Status", empty: "",
      options: [{ value: "", label: "All Status" }, ...statusOptions.map((o) => ({ value: o, label: o }))],
    },
    instructorName: {
      label: "Interviewer", empty: "",
      options: [{ value: "", label: "All Interviewers" }, ...instructorOptions],
    },
  });
  /* ⚠️ Memoised: a useEffect dependency, and a bare literal would loop. */
  const filterValues = listFilters.values;
  const filters = useMemo(() => ({ ...filterValues, search }), [filterValues, search]);

  useEffect(() => {
    const fetchInterviews = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          pageSize,
          ...(filters.instructorName && { instructorName: filters.instructorName }),
          ...(filters.currentStatus && { currentStatus: filters.currentStatus }),
          ...(filters.search && { search: filters.search }),
        };
        const { data } = await apiClient.get("/interviews/list", { params });
        setInterviews(data.interviews || []);
        setTotal(data.total || 0);
        setInstructorOptions((data.filters?.instructors || []).map(i => ({
          value: i._id,
          label: i.name,
        })));
        setStatusOptions(data.filters?.currentStatus || []);
      } catch {
        setToast({ show: true, message: "Failed to load interviews.", type: "error" });
      }
      setLoading(false);
    };
    fetchInterviews();
  }, [filters, page, pageSize]);

  useEffect(() => { setPage(1); }, [filters]);

  const handleSearchBlur = () => {
    if (searchInput !== search) setSearch(searchInput);
  };

  return (
    <div className="iv-page">
      <PageHeader
        title="Interviews"
        description={`Manage ${total} candidate interviews`}
        icon={CalendarCheck}
        actions={
          can("interviews.manage") && (
            <AppButton onClick={() => navigate("/interviews/new")}>
              <Plus size={16} />
              <span>Schedule Interview</span>
            </AppButton>
          )
        }
      />

      {/* Toolbar */}
      <div className="iv-toolbar">
        <div className="iv-search">
          <Search size={16} className="iv-search__icon" />
          <input
            type="text"
            className="iv-search__input"
            placeholder="Search students..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={e => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <FilterButton count={listFilters.count} onClick={listFilters.open} />
      </div>

      <ActiveFilterChips items={listFilters.activeFilters} onClearAll={listFilters.reset} />

      <FilterPanel {...listFilters.panelProps} title="Filter Interviews">
        <FilterSelectField {...listFilters.selectProps("currentStatus")} />
        <FilterSelectField {...listFilters.selectProps("instructorName")} />
      </FilterPanel>

      <InterviewList
        data={interviews}
        loading={loading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        total={total}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default InterviewsPage;
